/**
 * CvStep.jsx — RC 1.40 extraction-first onboarding upload
 *               with RC 1.40-debug visible diagnostics + bulletproof
 *               mobile input pattern.
 *
 * Mobile reliability changes vs. the previous label/sr-only attempt:
 *
 *   1. INPUT-AS-OVERLAY. The native <input type="file"> is positioned
 *      absolutely across the entire upload card with opacity:0. The
 *      user's tap directly hits the input — no <label htmlFor>
 *      indirection, no `display:none`, no `sr-only` clip-rect that
 *      iOS Safari sometimes refuses to fire from a label. This is the
 *      same pattern Stripe / Notion / Linear use for mobile-safe file
 *      pickers.
 *
 *   2. accept lists BOTH MIME types AND extensions (mobile pickers
 *      grey-out files when only extensions are listed).
 *
 *   3. Picker-tap detection: the input's `onClick` records a tap
 *      event so we can tell the difference between "user never tapped"
 *      and "user tapped but onChange never fired".
 */

import { useRef, useState } from 'react';
import {
  Upload, FileText, Trash2, AlertCircle, Loader2, CheckCircle2, ShieldAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';

// RC 1.41.H2 — UploadDebugPanel retired (RC 1.40 mobile upload is
// stable). The dx.event() / dx.reset() call-sites scattered through
// this file were lightweight console-mirror diagnostics; we keep
// them as no-ops so the call surface doesn't have to change.
const useUploadDebug = () => ({
  state: {},
  event: () => {},
  reset: () => {},
});

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const ACCEPTED_ATTR = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.pdf', '.doc', '.docx',
].join(',');

function prettyBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function extOf(name) {
  const lower = (name || '').toLowerCase();
  const i = lower.lastIndexOf('.');
  return i >= 0 ? lower.slice(i + 1) : '';
}

export default function CvStep({ data, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const inputRef = useRef(null);
  const dx = useUploadDebug('onboarding-cv');
  // RC 1.49.x SOURCE_CV_NEVER_PREVIEWABLE (operator-locked, 2026-05-06)
  // — the post-upload Source-CV preview affordance has been retired
  // globally. Source CVs are private/reference-only material; only
  // generated artefacts (Tailored CV variants, cover letters,
  // Interview Intelligence) are previewable / exportable.

  const handleFiles = async (files) => {
    const file = files?.[0];
    dx.event('file_selected', !!file);
    if (!file) {
      // The user opened the picker and dismissed without selecting.
      return;
    }

    const ext = extOf(file.name);
    dx.event('file_meta', { name: file.name, size: file.size, ext });

    // Client-side validation: extension + size only. Backend dual-checks.
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      dx.event('validation_ok', false);
      setUploadError('Please upload a PDF or DOCX file.');
      return;
    }
    if (ext === 'doc') {
      dx.event('validation_ok', false);
      setUploadError('Legacy .doc isn\'t supported. Please re-export as .docx or .pdf.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      dx.event('validation_ok', false);
      setUploadError(`File is too large (${prettyBytes(file.size)}). Max 10 MB.`);
      return;
    }
    dx.event('validation_ok', true);

    setUploadError('');
    setUploading(true);
    dx.event('upload_started', true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('original_filename', file.name);
      fd.append('make_primary', 'true');

      dx.event('upload_endpoint', '/api/base-cvs/upload');

      const result = await apiFetch('/api/base-cvs/upload', {
        method: 'POST',
        body: fd,
      });

      dx.event('http_status', 200);
      dx.event('parsed_text_len', { len: result?.text_length || 0 });

      if (result?.parse_status === 'empty') {
        setUploadError(
          'We uploaded your CV but couldn\'t read any text from it — it may be a scanned image. Try a different export, or skip and add it later.'
        );
      }

      onChange({
        cv_id: result.cv_id,
        original_filename: result.original_filename || file.name,
        size_bytes: file.size,
        text_length: result.text_length || 0,
        parse_status: result.parse_status,
        is_primary: !!result.is_primary,
      });
    } catch (err) {
      const status = err?.status || 0;
      const body =
        (err?.data && (err.data.detail || JSON.stringify(err.data))) ||
        err?.message || 'Upload failed.';
      dx.event('http_status', status);
      dx.event('parsed_text_len', { error: body });
      setUploadError(typeof body === 'string' ? body : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    onChange(null);
    setUploadError('');
    dx.reset();
    if (inputRef.current) inputRef.current.value = '';
  };

  // ─── Drag-and-drop on desktop ──────────────────────────────────
  const [isDragging, setIsDragging] = useState(false);
  const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const onDragLeave = () => setIsDragging(false);
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    handleFiles(e.dataTransfer?.files);
  };

  const hasFile = !!data?.cv_id;

  return (
    <div className="space-y-4 min-w-0 max-w-full overflow-x-hidden" data-testid="onboarding-cv-step">
      {/* RC 1.41 P0.1.C-r9 + RC 1.49.x SOURCE_CV_REPLACE_OPTION_A —
          permanence warning. CV upload here is the canonical first
          entry point. The /documents Source CV tab also accepts
          replacement uploads (Option A — adds a new primary, leaves
          history intact). The "private reference material / not
          previewable / not exported" stance still applies globally. */}
      <div
        className="flex items-start gap-2 rounded-lg border border-ndor-gold/40 bg-ndor-gold/5 px-3 py-2.5"
        data-testid="cv-onboarding-permanence-note"
      >
        <ShieldAlert className="h-4 w-4 text-ndor-gold mt-0.5 shrink-0" />
        <p className="text-xs text-ndor-ink-secondary leading-relaxed">
          <strong>Choose carefully — this becomes your Source CV.</strong> NDORFlow reads it to understand your experience, but your uploaded Source CV is private reference material and is not previewed or exported. You can upload a new Source CV later from Documents if your experience changes.
        </p>
      </div>

      {uploadError && (
        <div
          className="flex items-start gap-2 rounded-lg bg-ndor-error-light border border-ndor-error-border p-3"
          data-testid="cv-upload-error"
        >
          <AlertCircle className="h-4 w-4 text-ndor-error mt-0.5 shrink-0" />
          <p className="text-sm text-ndor-ink-secondary leading-relaxed">{uploadError}</p>
        </div>
      )}

      {hasFile ? (
        <div
          className="rounded-xl border border-ndor-edge bg-ndor-surface-alt/40 p-4 flex items-center gap-3"
          data-testid="cv-uploaded-card"
        >
          <div className="w-11 h-11 rounded-lg bg-ndor-success-light flex items-center justify-center shrink-0">
            <CheckCircle2 className="h-5 w-5 text-ndor-success" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ndor-ink truncate" data-testid="cv-uploaded-filename">
              {data.original_filename}
            </p>
            <p className="text-xs text-ndor-ink-muted">
              {data.parse_status === 'success' && data.text_length
                ? `Read ${data.text_length.toLocaleString()} characters`
                : data.parse_status === 'empty'
                  ? 'Couldn\'t read text — you can re-upload from your dashboard'
                  : 'Saved'}
              {typeof data.size_bytes === 'number' && ` · ${prettyBytes(data.size_bytes)}`}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleRemove}
            data-testid="cv-remove-button"
            className="text-ndor-ink-muted hover:text-ndor-error shrink-0"
            aria-label="Remove uploaded CV"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        // INPUT-AS-OVERLAY pattern. The <input> is full-card and
        // opacity:0 — the user's tap hits the input directly, with
        // no label indirection. The visual layer is non-interactive.
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={
            'relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all text-center ' +
            (isDragging
              ? 'border-ndor-gold bg-ndor-gold/5'
              : 'border-ndor-edge hover:border-ndor-ink-muted bg-ndor-surface/30') +
            ' py-10 px-5 sm:py-14'
          }
          data-testid="cv-upload-zone"
        >
          {/* The native input — full-card, opacity:0, sits ON TOP. */}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_ATTR}
            onClick={() => dx.event('picker_tapped', true)}
            onChange={(e) => handleFiles(e.target.files)}
            disabled={uploading}
            data-testid="cv-file-input"
            aria-label="Choose your CV file"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10"
          />

          {/* Visual layer — pointer-events:none so taps fall through
              to the input above it. */}
          <div className="pointer-events-none flex flex-col items-center">
            {uploading ? (
              <Loader2 className="h-8 w-8 animate-spin text-ndor-ink-muted mb-3" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-ndor-surface-alt flex items-center justify-center mb-3">
                <Upload className="h-5 w-5 text-ndor-ink-muted" />
              </div>
            )}
            <p className="text-sm font-medium text-ndor-ink">
              {uploading ? 'Reading your CV…' : 'Upload your Source CV'}
            </p>
            <p className="text-xs text-ndor-ink-secondary mt-1 max-w-xs">
              NDORFlow uses this as your baseline and improves it through generated CVs.
            </p>
            <p className="text-xs text-ndor-ink-muted mt-2 hidden sm:block">
              or drag &amp; drop here
            </p>
            <p className="text-xs text-ndor-ink-muted mt-2">PDF · DOCX · max 10 MB</p>
          </div>
        </div>
      )}

      <p className="text-xs text-ndor-ink-muted flex items-start gap-1.5 leading-relaxed">
        <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          We read your CV to pull skills, years of experience, location and seniority — so you don't have to type it twice.
          This becomes your Source CV — every tailored CV variant and cover letter NDORFlow generates is built from it.
        </span>
      </p>

      {/* RC 1.41.H2 — UploadDebugPanel retired. Mobile CV upload is
          confirmed stable on real hardware via the RC 1.40 input-as-
          overlay + AuthContext-stable patterns. */}

      {/* RC 1.49.x SOURCE_CV_NEVER_PREVIEWABLE — the post-upload
          Source-CV preview modal has been retired. */}
    </div>
  );
}
